'use client';

import { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import {
  getBrandHomeUrl,
  getBrandAboutUrl,
  getBrandTeamUrl,
  getBrandCareersUrl,
  getBrandContactUrl,
  getBrandInvestorsUrl,
  getCreatorLoginUrl,
  getCreatorRegisterUrl,
} from '@/lib/creatorPlatformUrls';

const ABOUT_LINKS = [
  { href: () => getBrandAboutUrl(), label: '品牌介绍' },
  { href: () => getBrandTeamUrl(), label: '创始团队' },
  { href: () => getBrandCareersUrl(), label: '加入我们' },
  { href: () => getBrandContactUrl(), label: '联系我们' },
] as const;

const PRODUCT_LINKS = [
  { href: getCreatorLoginUrl, label: '创作者平台' },
  { href: getBrandInvestorsUrl, label: '更多产品服务' },
] as const;

type MarketingHeaderProps = { atHeroPage?: boolean };

export default function MarketingHeader({ atHeroPage }: MarketingHeaderProps = {}) {
  const [openDropdown, setOpenDropdown] = useState<'about' | 'product' | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpand, setMobileExpand] = useState<'about' | 'product' | null>(null);
  const aboutRef = useRef<HTMLDivElement>(null);
  const productRef = useRef<HTMLDivElement>(null);

  /* 点击外部关闭桌面下拉 */
  useEffect(() => {
    if (openDropdown === null) return;
    const close = (e: MouseEvent) => {
      const t = e.target as Node;
      if (aboutRef.current?.contains(t) || productRef.current?.contains(t)) return;
      setOpenDropdown(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [openDropdown]);

  /* Esc 关闭下拉或移动菜单 */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (mobileOpen) setMobileOpen(false);
      else setOpenDropdown(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileOpen]);

  const linkClass = 'link-hover';
  const navStyle = { fontSize: '0.9375rem', fontWeight: 500 };

  return (
    <header className={`home-header${atHeroPage ? ' at-hero-page' : ''}`} id="home-header">
      <div className="home-header-left">
        <Link href={getBrandHomeUrl()} className="home-header-logo">
          <span className="home-header-logo-imgs">
            <Image src="/logo.png" alt="基音" width={36} height={36} className="home-header-logo-default" style={{ borderRadius: 6 }} priority />
            <Image src="/logo-white.png" alt="" width={36} height={36} className="home-header-logo-white" style={{ borderRadius: 6 }} />
          </span>
          <div className="home-header-logo-text">
            <div className="home-header-logo-zh">基音</div>
            <div className="home-header-logo-en">FUNDAMUSE</div>
          </div>
        </Link>
        <nav className="home-header-nav-left" aria-label="主导航">
          <div className="home-header-dropdown" ref={aboutRef}>
          <button
            type="button"
            className={`${linkClass} home-header-dropdown-trigger`}
            style={navStyle}
            aria-haspopup="true"
            aria-expanded={openDropdown === 'about'}
            onClick={() => setOpenDropdown((v) => (v === 'about' ? null : 'about'))}
          >
            关于我们
          </button>
          <div className="home-header-dropdown-panel" hidden={openDropdown !== 'about'}>
            {ABOUT_LINKS.map((item) => (
              <Link key={item.label} href={item.href()} className="home-header-dropdown-item" onClick={() => setOpenDropdown(null)}>
                {item.label}
              </Link>
            ))}
          </div>
        </div>

        {/* 桌面：产品 下拉 */}
        <div className="home-header-dropdown" ref={productRef}>
          <button
            type="button"
            className={`${linkClass} home-header-dropdown-trigger`}
            style={navStyle}
            aria-haspopup="true"
            aria-expanded={openDropdown === 'product'}
            onClick={() => setOpenDropdown((v) => (v === 'product' ? null : 'product'))}
          >
            产品服务
          </button>
          <div className="home-header-dropdown-panel" hidden={openDropdown !== 'product'}>
            {PRODUCT_LINKS.map((item) => (
              <Link key={item.label} href={item.href()} className="home-header-dropdown-item" onClick={() => setOpenDropdown(null)}>
                {item.label}
              </Link>
            ))}
          </div>
        </div>
        <Link href={getBrandContactUrl()} className={linkClass} style={navStyle}>
          联系我们
        </Link>
        </nav>
      </div>

      <nav className="home-header-nav" aria-label="主导航">
        <Link href={getCreatorLoginUrl()} className={`${linkClass} home-header-nav-login`} style={navStyle}>
          登录
        </Link>
        <Link href={getCreatorRegisterUrl()} className="home-cta-button" style={{ padding: '0.5rem 1rem', fontSize: '0.9375rem' }}>
          注册
        </Link>
      </nav>

      {/* 移动端：仅 Logo + 注册 + 汉堡 */}
      <div className="home-header-mobile">
        <Link href={getCreatorRegisterUrl()} className="home-cta-button" style={{ padding: '0.5rem 1rem', fontSize: '0.9375rem' }}>
          注册
        </Link>
        <button
          type="button"
          className="home-header-hamburger"
          aria-label={mobileOpen ? '关闭菜单' : '打开菜单'}
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((v) => !v)}
        >
          <span className="home-header-hamburger-bar" />
          <span className="home-header-hamburger-bar" />
          <span className="home-header-hamburger-bar" />
        </button>
      </div>

      {/* 移动端全屏菜单 */}
      <div
        className="home-header-mobile-menu"
        aria-hidden={!mobileOpen}
        hidden={!mobileOpen}
        onClick={() => setMobileOpen(false)}
        role="dialog"
        aria-modal="true"
      >
        <div className="home-header-mobile-menu-inner" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="home-header-mobile-item home-header-mobile-expand"
            aria-expanded={mobileExpand === 'about'}
            onClick={() => setMobileExpand((v) => (v === 'about' ? null : 'about'))}
          >
            关于我们
          </button>
          {mobileExpand === 'about' && (
            <div className="home-header-mobile-sub">
              {ABOUT_LINKS.map((item) => (
                <Link key={item.label} href={item.href()} className="home-header-mobile-sub-item" onClick={() => setMobileOpen(false)}>
                  {item.label}
                </Link>
              ))}
            </div>
          )}
          <button
            type="button"
            className="home-header-mobile-item home-header-mobile-expand"
            aria-expanded={mobileExpand === 'product'}
            onClick={() => setMobileExpand((v) => (v === 'product' ? null : 'product'))}
          >
            产品服务
          </button>
          {mobileExpand === 'product' && (
            <div className="home-header-mobile-sub">
              {PRODUCT_LINKS.map((item) => (
                <Link key={item.label} href={item.href()} className="home-header-mobile-sub-item" onClick={() => setMobileOpen(false)}>
                  {item.label}
                </Link>
              ))}
            </div>
          )}
          <Link href={getBrandContactUrl()} className="home-header-mobile-item" onClick={() => setMobileOpen(false)}>
            联系我们
          </Link>
          <Link href={getCreatorLoginUrl()} className="home-header-mobile-item" onClick={() => setMobileOpen(false)}>
            登录
          </Link>
        </div>
      </div>
    </header>
  );
}
